import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { trackEvent } from "../plausible";

const HUBSPOT_FORM_SCRIPT =
    "https://js-eu1.hsforms.net/forms/embed/developer/25477516.js";
const HUBSPOT_PORTAL_ID = "25477516";
const HUBSPOT_DOCUMENT_FORM_ID = "04b23c8c-5295-4f1a-9bcf-adbbf7494bb6";
const HUBSPOT_PDF_PROPERTY_NAME = "pdf_download_link";
const HUBSPOT_PDF_FIELD_NAME = `0-1/${HUBSPOT_PDF_PROPERTY_NAME}`;

const getHubspotFormFromEvent = (event) => {
    if (typeof window === "undefined") {
        return null;
    }

    return window.HubSpotFormsV4?.getFormFromEvent?.(event) || null;
};

const getDocumentDownloadForm = () => {
    if (typeof window === "undefined") {
        return null;
    }

    const documentDownloadForms = (
        window.HubSpotFormsV4?.getForms?.() || []
    ).filter((form) => form.getFormId?.() === HUBSPOT_DOCUMENT_FORM_ID);

    // HubSpot keeps old form instances after Gatsby client-side navigation.
    // The last registered instance belongs to the currently rendered modal.
    return documentDownloadForms[documentDownloadForms.length - 1] || null;
};

const isFormEvent = (event, formId) => {
    const eventFormId =
        event?.detail?.formId || event?.detail?.formID || event?.detail?.id;

    return !eventFormId || eventFormId === formId;
};

const setNativeFieldValue = (field, value) => {
    field.value = value;
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
};

const setPdfUrlInFormDom = (pdfUrl) => {
    const formRoot = document.querySelector(
        `.hubspot-form-modal__form[data-form-id="${HUBSPOT_DOCUMENT_FORM_ID}"]`
    );
    const field = formRoot?.querySelector(
        `[name="${HUBSPOT_PDF_FIELD_NAME}"], [name="${HUBSPOT_PDF_PROPERTY_NAME}"]`
    );

    if (field && pdfUrl) {
        setNativeFieldValue(field, pdfUrl);
        return true;
    }

    return false;
};

const setPdfUrlInHubspotForm = (pdfUrl, event) => {
    if (!pdfUrl || (event && !isFormEvent(event, HUBSPOT_DOCUMENT_FORM_ID))) {
        return false;
    }

    const form = event
        ? getHubspotFormFromEvent(event)
        : getDocumentDownloadForm();
    let wasSetWithApi = false;

    try {
        if (form?.setFieldValue) {
            form.setFieldValue(HUBSPOT_PDF_FIELD_NAME, [pdfUrl]);
            wasSetWithApi = true;
        }
    } catch {
        // DOM fallback below supports forms without the V4 API.
    }

    const wasSetInDom = setPdfUrlInFormDom(pdfUrl);

    return wasSetWithApi || wasSetInDom;
};

const HubspotFormModal = ({
    isOpen,
    onClose,
    pageTitle,
    onSuccess,
    header,
    description,
    pdfUrl,
    formId = HUBSPOT_DOCUMENT_FORM_ID,
    eyebrow = "Wyden Insights",
    trackingEventName = "Contact Form: Submitted",
}) => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const modalHeader = header || pageTitle || "Download Our Client Case";

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleReady = (event) => {
            setPdfUrlInHubspotForm(pdfUrl, event);
        };

        const handleSuccess = (event) => {
            if (!isFormEvent(event, formId)) {
                return;
            }

            setIsSubmitted(true);
            trackEvent(trackingEventName, {
                props: {
                    "contact-form-page-title": pageTitle,
                },
            });
            onSuccess?.();
        };

        window.addEventListener("hs-form-event:on-ready", handleReady);
        window.addEventListener(
            "hs-form-event:on-submission:success",
            handleSuccess
        );
        return () => {
            window.removeEventListener("hs-form-event:on-ready", handleReady);
            window.removeEventListener(
                "hs-form-event:on-submission:success",
                handleSuccess
            );
        };
    }, [formId, isOpen, onSuccess, pageTitle, pdfUrl, trackingEventName]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        const previousOverflow = document.body.style.overflow;

        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen || !pdfUrl) {
            return undefined;
        }

        let retryTimeout;
        let retryCount = 0;
        const maxRetries = 30;

        setIsSubmitted(false);

        const setPdfUrlWhenFormIsReady = () => {
            const wasSet = setPdfUrlInHubspotForm(pdfUrl);

            if (!wasSet && retryCount < maxRetries) {
                retryCount += 1;
                retryTimeout = window.setTimeout(setPdfUrlWhenFormIsReady, 100);
            }
        };

        setPdfUrlWhenFormIsReady();

        return () => {
            window.clearTimeout(retryTimeout);
        };
    }, [isOpen, pdfUrl]);

    useEffect(() => {
        const script = document.createElement("script");

        script.src = HUBSPOT_FORM_SCRIPT;
        script.defer = true;
        script.dataset.hubspotModalFormScript = "true";
        document.body.appendChild(script);

        return () => {
            script.remove();
        };
    }, []);

    const modalContent = (
        <div
            className={`hubspot-form-modal ${
                isOpen ? "hubspot-form-modal--open" : ""
            } ${isSubmitted ? "hubspot-form-modal--submitted" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-label={pageTitle || "Get in touch"}
            aria-hidden={!isOpen}
        >
            <button
                className="hubspot-form-modal__backdrop"
                type="button"
                aria-label="Close contact form"
                onClick={onClose}
            />
            <div className="hubspot-form-modal__dialog">
                <button
                    className="hubspot-form-modal__close"
                    type="button"
                    aria-label="Close contact form"
                    onClick={onClose}
                >
                    <span aria-hidden="true">&times;</span>
                </button>
                <div className="hubspot-form-modal__header">
                    <p className="hubspot-form-modal__eyebrow">{eyebrow}</p>
                    <h2
                        className="hubspot-form-modal__title"
                        dangerouslySetInnerHTML={{ __html: modalHeader }}
                    />
                    {description && (
                        <div
                            className="hubspot-form-modal__description"
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    )}
                </div>
                <div
                    className="hs-form-html hubspot-form-modal__form"
                    data-region="eu1"
                    data-form-id={formId}
                    data-portal-id={HUBSPOT_PORTAL_ID}
                ></div>
                {isSubmitted && (
                    <div className="hubspot-form-modal__success-actions">
                        <button type="button" onClick={onClose}>
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            {typeof document !== "undefined"
                ? createPortal(modalContent, document.body)
                : null}
        </>
    );
};

export default HubspotFormModal;
